import type { Chart, ChartOptions, Plugin, ScaleChartOptions } from 'chart.js'

import { CHART_PRIMARY, colorAt } from './chartPalette'
import { GROUPED_HORIZONTAL_SPACER_ROWS } from './chartData'

/** Figma 5897:14748 — drill-down chart card + typography tokens. */
export const DRILLDOWN_CHART_CARD_SX = {
  bgcolor: '#FFFFFF',
  border: '1px solid #E6E8EC',
  borderRadius: '10px',
  p: '16px 20px',
} as const

export const DRILLDOWN_CHART_TITLE_SX = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '14px',
  lineHeight: '21px',
  color: '#0F172A',
  wordWrap: 'break-word',
} as const

/** Plot area height inside a ~300px Figma card (title + legend reserved). */
export const DRILLDOWN_CHART_PLOT_HEIGHT = 248

/** Figma 5897:16596 — word cloud card plot area (~440px card minus padding/title). */
export const DRILLDOWN_WORD_CLOUD_PLOT_HEIGHT = 360

/** Figma 5897:16596 — word cloud typography + colour tokens. */
export const WORD_CLOUD_FONT_FAMILY = 'Inter, sans-serif'
export const WORD_CLOUD_MIN_FONT = 24
export const WORD_CLOUD_MAX_FONT = 72
export const WORD_CLOUD_PADDING = 4

/** Word colours — cycle the shared chart palette. */
export function wordCloudColorAt(index: number): string {
  return colorAt(index)
}

/** Map normalised frequency (0–1) to Inter weight tiers from Figma samples. */
export function wordCloudWeightForRatio(ratio: number): number {
  if (ratio >= 0.85) return 800
  if (ratio >= 0.6) return 700
  if (ratio >= 0.35) return 400
  if (ratio >= 0.15) return 300
  return 200
}

/** Title-case each token — matches Figma `capitalize` on multi-word labels. */
export function wordCloudDisplayLabel(raw: string): string {
  return raw
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')
}

const AXIS_FONT = { family: 'Inter, sans-serif', size: 10, weight: 'normal' as const }
const LEGEND_FONT = { family: 'Inter, sans-serif', size: 11, weight: 'normal' as const }

/** Vertical gap between the legend row and the chart plot (all drill-down charts). */
export const DRILLDOWN_LEGEND_TO_CHART_GAP_PX = 16

/** Horizontal gap between legend items (Female · Male · Non-binary, …). */
export const DRILLDOWN_LEGEND_ITEM_GAP_PX = 20

const patchedLegendInstances = new WeakSet<object>()

type LegendWithFit = {
  fit: () => void
  height: number
  options: { display?: boolean }
  legendItems?: unknown[]
}

function patchLegendFitForChartGap(chart: Chart): void {
  const legend = chart.legend as LegendWithFit | undefined
  if (!legend || patchedLegendInstances.has(legend)) return

  const originalFit = legend.fit.bind(legend)
  legend.fit = function fitWithChartGap() {
    originalFit()
    if (this.options.display && (this.legendItems?.length ?? 0) > 0) {
      this.height += DRILLDOWN_LEGEND_TO_CHART_GAP_PX
    }
  }
  patchedLegendInstances.add(legend)
}

/** Reserves empty space below the legend before the plot (layout.padding does not). */
export const drilldownLegendGapPlugin: Plugin = {
  id: 'drilldownLegendGap',
  afterInit(chart) {
    patchLegendFitForChartGap(chart)
  },
  beforeUpdate(chart) {
    patchLegendFitForChartGap(chart)
  },
}

function setLegendHoverCursor(event: { native?: Event | null }, cursor: string): void {
  const target = event.native?.target
  if (target instanceof HTMLElement) {
    target.style.cursor = cursor
  }
}

const LEGEND_DIM_OPACITY = 0.28

type ChartWithLegendColors = Chart & {
  $drilldownLegendBaseColors?: Array<string | string[]>
  $drilldownLegendHoveredIndex?: number | null
}

function parseHexColor(hex: string): { r: number; g: number; b: number } | null {
  const normalized = hex.replace('#', '')
  if (normalized.length !== 6) return null
  const r = Number.parseInt(normalized.slice(0, 2), 16)
  const g = Number.parseInt(normalized.slice(2, 4), 16)
  const b = Number.parseInt(normalized.slice(4, 6), 16)
  if ([r, g, b].some((v) => Number.isNaN(v))) return null
  return { r, g, b }
}

function fadeChartColor(color: string, alpha: number): string {
  if (color.startsWith('rgba(')) return color
  const rgb = parseHexColor(color)
  if (!rgb) return color
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`
}

function snapshotLegendBaseColors(chart: Chart): void {
  const store = chart as ChartWithLegendColors
  if (store.$drilldownLegendBaseColors) return
  store.$drilldownLegendBaseColors = chart.data.datasets.map((dataset) => {
    const bg = dataset.backgroundColor
    if (Array.isArray(bg)) return [...bg]
    return typeof bg === 'string' ? bg : CHART_PRIMARY
  })
}

function applyLegendSeriesHighlight(chart: Chart, hoveredIndex: number | null): void {
  const store = chart as ChartWithLegendColors
  snapshotLegendBaseColors(chart)
  store.$drilldownLegendHoveredIndex = hoveredIndex
  const baseColors = store.$drilldownLegendBaseColors ?? []

  chart.data.datasets.forEach((dataset, datasetIndex) => {
    const base = baseColors[datasetIndex]
    if (Array.isArray(base)) {
      dataset.backgroundColor = base.map((color) =>
        hoveredIndex === null || datasetIndex === hoveredIndex
          ? color
          : fadeChartColor(String(color), LEGEND_DIM_OPACITY),
      )
      if ('borderColor' in dataset && Array.isArray(dataset.borderColor)) {
        dataset.borderColor = (dataset.borderColor as string[]).map((color) =>
          hoveredIndex === null || datasetIndex === hoveredIndex
            ? color
            : fadeChartColor(String(color), LEGEND_DIM_OPACITY),
        )
      }
      return
    }

    const color = String(base ?? CHART_PRIMARY)
    dataset.backgroundColor =
      hoveredIndex === null || datasetIndex === hoveredIndex
        ? color
        : fadeChartColor(color, LEGEND_DIM_OPACITY)
    if ('borderColor' in dataset && typeof dataset.borderColor === 'string') {
      dataset.borderColor =
        hoveredIndex === null || datasetIndex === hoveredIndex
          ? dataset.borderColor
          : fadeChartColor(dataset.borderColor, LEGEND_DIM_OPACITY)
    }
  })

  chart.update('none')
}

function handleLegendHover(
  event: { native?: Event | null },
  legendItem: { datasetIndex?: number },
  legend: { chart: Chart },
): void {
  setLegendHoverCursor(event, 'pointer')
  const idx = legendItem.datasetIndex
  if (idx === undefined) return
  applyLegendSeriesHighlight(legend.chart, idx)
}

function handleLegendLeave(
  event: { native?: Event | null },
  _legendItem: unknown,
  legend: { chart: Chart },
): void {
  setLegendHoverCursor(event, 'default')
  applyLegendSeriesHighlight(legend.chart, null)
}

export function drilldownChartLayoutPadding(
  overrides?: Partial<{ top: number; left: number; right: number; bottom: number }>,
) {
  return {
    padding: {
      top: 4,
      left: 4,
      right: 8,
      bottom: 4,
      ...overrides,
    },
  }
}

const GRID = {
  color: '#E6E8EC',
  lineWidth: 1,
  drawTicks: false,
  tickLength: 0,
}

function cartesianScales(
  indexAxis: 'x' | 'y',
  stacked: boolean,
): ScaleChartOptions<'bar' | 'line'>['scales'] {
  const valueGrid = indexAxis === 'y'
  const noBorder = { display: false, dash: [], dashOffset: 0, color: 'transparent', width: 0, z: 0 }
  return {
    x: {
      stacked,
      beginAtZero: true,
      border: noBorder,
      grid: {
        display: valueGrid,
        ...GRID,
      },
      ticks: {
        color: '#6B7280',
        font: AXIS_FONT,
        precision: 0,
        padding: 8,
      },
    },
    y: {
      stacked,
      beginAtZero: true,
      border: noBorder,
      grid: {
        display: !valueGrid,
        ...GRID,
      },
      ticks: {
        color: '#6B7280',
        font: AXIS_FONT,
        precision: 0,
        padding: 8,
      },
    },
  } as unknown as ScaleChartOptions<'bar' | 'line'>['scales']
}

export function drilldownLegend(show: boolean): ChartOptions['plugins'] {
  return {
    legend: {
      display: show,
      position: 'top',
      align: 'start',
      onHover: handleLegendHover,
      onLeave: handleLegendLeave,
      labels: {
        color: '#6B7280',
        font: LEGEND_FONT,
        boxWidth: 16,
        boxHeight: 7,
        borderRadius: 2,
        padding: DRILLDOWN_LEGEND_ITEM_GAP_PX,
        useBorderRadius: true,
      },
    },
  }
}

export function drilldownBarOptions(
  indexAxis: 'x' | 'y',
  stacked: boolean,
  showLegend: boolean,
  layout: DrilldownBarLayout = 'simple',
): ChartOptions<'bar'> {
  const orientation = indexAxis === 'x' ? 'vertical' : 'horizontal'
  const barDefaults = drilldownBarDatasetDefaults(orientation, layout)
  return {
    indexAxis,
    responsive: true,
    maintainAspectRatio: false,
    layout: drilldownChartLayoutPadding(),
    datasets: {
      bar: { ...barDefaults },
    },
    plugins: {
      ...drilldownLegend(showLegend),
      tooltip: {
        backgroundColor: '#FFFFFF',
        borderColor: '#E6E8EC',
        borderWidth: 1,
        titleColor: '#23262F',
        bodyColor: '#6B7280',
        titleFont: { family: 'Inter, sans-serif', size: 12, weight: 'bold' },
        bodyFont: { family: 'Inter, sans-serif', size: 11 },
        padding: 10,
        cornerRadius: 5,
        displayColors: true,
        boxWidth: 8,
        boxHeight: 8,
        boxPadding: 4,
      },
    },
    scales: cartesianScales(indexAxis, stacked),
  }
}

export function drilldownDoughnutOptions(
  tooltipBorderColor: string = DONUT_TOOLTIP_BORDER_MULTI,
): ChartOptions<'doughnut'> {
  return {
    responsive: true,
    maintainAspectRatio: false,
    layout: drilldownChartLayoutPadding({ bottom: 8, left: 8 }),
    elements: {
      arc: {
        borderWidth: 0,
        hoverBorderWidth: 0,
        spacing: 0,
      },
    },
    plugins: {
      ...drilldownLegend(true),
      tooltip: {
        backgroundColor: '#FFFFFF',
        borderColor: tooltipBorderColor,
        borderWidth: 1,
        titleColor: '#23262F',
        bodyColor: '#23262F',
        titleFont: { family: 'Inter, sans-serif', size: 8, weight: 'normal' },
        bodyFont: { family: 'Inter, sans-serif', size: 8, weight: 'bold' },
        padding: { top: 8, right: 10, bottom: 8, left: 10 },
        cornerRadius: 5,
        displayColors: true,
        usePointStyle: true,
        boxWidth: 7,
        boxHeight: 7,
        boxPadding: 4,
      },
    },
  }
}

export function drilldownLineOptions(
  showLegend: boolean,
  area: boolean,
): ChartOptions<'line' | 'bar'> {
  return {
    responsive: true,
    maintainAspectRatio: false,
    layout: drilldownChartLayoutPadding(),
    plugins: {
      ...drilldownLegend(showLegend),
      tooltip: {
        backgroundColor: '#FFFFFF',
        borderColor: '#E6E8EC',
        borderWidth: 1,
        titleColor: '#23262F',
        bodyColor: '#6B7280',
        titleFont: { family: 'Inter, sans-serif', size: 12, weight: 'bold' },
        bodyFont: { family: 'Inter, sans-serif', size: 11 },
        padding: 10,
        cornerRadius: 5,
      },
    },
    scales: cartesianScales('x', false),
    elements: {
      line: {
        tension: 0.35,
        borderWidth: 2,
        fill: area,
      },
      point: {
        radius: 4,
        hoverRadius: 5,
        borderWidth: 0,
      },
    },
  }
}

/** Figma horizontal bar end-cap radius (5897:14387). */
export const DRILLDOWN_HORIZONTAL_BAR_RADIUS = 5
/** Figma vertical column bar corner radius. */
export const DRILLDOWN_BAR_RADIUS = 2
/** Figma 5897:15117 — horizontal bar thickness (row height). */
export const DRILLDOWN_BAR_THICKNESS = 36
export const DRILLDOWN_GROUPED_BAR_THICKNESS = 24
export const DRILLDOWN_VERTICAL_BAR_MAX_THICKNESS = 64
/** Fixed column width — grouped_column segment bars (flush within each category group). */
export const DRILLDOWN_VERTICAL_GROUPED_BAR_THICKNESS = 36
export const DRILLDOWN_VERTICAL_GROUPED_CATEGORY_PERCENTAGE = 0.78
export const DRILLDOWN_VERTICAL_BAR_PERCENTAGE = 0.92
export const DRILLDOWN_VERTICAL_CATEGORY_PERCENTAGE = 0.95
export const DRILLDOWN_HORIZONTAL_BAR_PERCENTAGE = 0.85
export const DRILLDOWN_HORIZONTAL_CATEGORY_PERCENTAGE = 0.92
/** Wider row spacing — multiple_stacked_horizontal_bar. */
export const DRILLDOWN_HORIZONTAL_SPACED_CATEGORY_PERCENTAGE = 0.72
export const HORIZONTAL_BAR_ROW_GAP_DEFAULT_PX = 8
export const HORIZONTAL_BAR_ROW_GAP_SPACED_PX = 16
/** Slightly wider row spacing — theme_frequency_bar (height is auto-sized). */
export const HORIZONTAL_BAR_ROW_GAP_THEME_FREQUENCY_PX = 12

/** Figma 5897:15117 — grouped / stacked horizontal plot padding (axis + legend + legend gap). */
export const GROUPED_HORIZONTAL_CHART_PADDING_PX = 72
/** Total height of one option's flush segment block (all group items combined). */
export const GROUPED_HORIZONTAL_GROUP_BLOCK_HEIGHT = 130
/** Row pitch for height calc — bar thickness + inter-row gap. */
function horizontalBarRowPitchPx(gapPx: number): number {
  return DRILLDOWN_BAR_THICKNESS + gapPx
}

/** horizontal_bar, stacked_horizontal_bar (all respondents) — 36px bars, default gap. */
export function drilldownHorizontalBarDatasetDefaults(): {
  barThickness: number
  maxBarThickness: number
  categoryPercentage: number
} {
  return {
    barThickness: DRILLDOWN_BAR_THICKNESS,
    maxBarThickness: DRILLDOWN_BAR_THICKNESS,
    categoryPercentage: DRILLDOWN_HORIZONTAL_CATEGORY_PERCENTAGE,
  }
}

/** multiple_stacked_horizontal_bar — 36px bars, wider row gap. */
export function drilldownSpacedHorizontalBarDatasetDefaults(): {
  barThickness: number
  maxBarThickness: number
  barPercentage: number
  categoryPercentage: number
} {
  return {
    barThickness: DRILLDOWN_BAR_THICKNESS,
    maxBarThickness: DRILLDOWN_BAR_THICKNESS,
    barPercentage: DRILLDOWN_HORIZONTAL_BAR_PERCENTAGE,
    categoryPercentage: DRILLDOWN_HORIZONTAL_SPACED_CATEGORY_PERCENTAGE,
  }
}

/** theme_frequency_bar — same bar sizing as horizontal_bar; row pitch drives gap via height. */
export function drilldownThemeFrequencyBarDatasetDefaults(): {
  barThickness: number
  categoryPercentage: number
} {
  return drilldownHorizontalBarDatasetDefaults()
}

export function drilldownStackedHorizontalBarDatasetDefaults(): {
  barThickness: number
  maxBarThickness: number
  barPercentage: number
  categoryPercentage: number
} {
  return drilldownSpacedHorizontalBarDatasetDefaults()
}

export function horizontalBarChartHeight(
  categoryCount: number,
  gapPx: number = HORIZONTAL_BAR_ROW_GAP_DEFAULT_PX,
): number {
  if (categoryCount === 0) {
    return DRILLDOWN_CHART_PLOT_HEIGHT
  }
  const contentHeight =
    GROUPED_HORIZONTAL_CHART_PADDING_PX + categoryCount * horizontalBarRowPitchPx(gapPx)
  // Size to data rows — avoid a tall plot that makes Chart.js stretch bars when counts are low.
  return Math.max(contentHeight, horizontalBarRowPitchPx(gapPx) + GROUPED_HORIZONTAL_CHART_PADDING_PX)
}

export function spacedHorizontalBarChartHeight(categoryCount: number): number {
  return horizontalBarChartHeight(categoryCount, HORIZONTAL_BAR_ROW_GAP_SPACED_PX)
}

export function themeFrequencyBarChartHeight(categoryCount: number): number {
  return horizontalBarChartHeight(categoryCount, HORIZONTAL_BAR_ROW_GAP_THEME_FREQUENCY_PX)
}

export function groupedHorizontalSegmentBarThickness(segmentCount: number): number {
  if (segmentCount <= 0) return DRILLDOWN_BAR_THICKNESS
  const perSegment = GROUPED_HORIZONTAL_GROUP_BLOCK_HEIGHT / segmentCount
  // Never exceed standard horizontal bar thickness — a single segment must not fill the 130px block.
  return Math.min(perSegment, DRILLDOWN_BAR_THICKNESS)
}

export function groupedHorizontalChartHeight(optionCount: number, segmentCount: number): number {
  if (optionCount === 0 || segmentCount === 0) {
    return DRILLDOWN_CHART_PLOT_HEIGHT
  }
  const segmentBarThickness = groupedHorizontalSegmentBarThickness(segmentCount)
  const dataRowCount = optionCount * segmentCount
  const spacerRowCount = Math.max(0, optionCount - 1) * GROUPED_HORIZONTAL_SPACER_ROWS
  // Chart.js gives every category index the same slot height — include spacer rows in the count.
  const totalRowCount = dataRowCount + spacerRowCount
  const contentHeight = GROUPED_HORIZONTAL_CHART_PADDING_PX + totalRowCount * segmentBarThickness
  return Math.max(
    contentHeight,
    segmentBarThickness + GROUPED_HORIZONTAL_CHART_PADDING_PX,
  )
}

/** grouped_horizontal_bar — segment rows flush within each option block. */
export function drilldownGroupedHorizontalBarDatasetDefaults(segmentCount = 4): {
  barThickness: number
  maxBarThickness: number
  barPercentage: number
  categoryPercentage: number
} {
  const thickness = groupedHorizontalSegmentBarThickness(segmentCount)
  return {
    barThickness: thickness,
    maxBarThickness: thickness,
    barPercentage: 1,
    categoryPercentage: 1,
  }
}

/** Grouped vertical columns — fixed thickness so segment bars cluster (no slot padding). */
export function drilldownGroupedVerticalBarDatasetDefaults(): {
  barThickness: number
  categoryPercentage: number
} {
  return {
    barThickness: DRILLDOWN_VERTICAL_GROUPED_BAR_THICKNESS,
    categoryPercentage: DRILLDOWN_VERTICAL_GROUPED_CATEGORY_PERCENTAGE,
  }
}

/** vertical_column + stacked_bar / grouped_stacked_bar — shared column width (Figma 5917:1467). */
export function drilldownVerticalColumnBarDatasetDefaults(): {
  barPercentage: number
  categoryPercentage: number
  maxBarThickness: number
} {
  return {
    barPercentage: DRILLDOWN_VERTICAL_BAR_PERCENTAGE,
    categoryPercentage: DRILLDOWN_VERTICAL_CATEGORY_PERCENTAGE,
    maxBarThickness: DRILLDOWN_VERTICAL_BAR_MAX_THICKNESS,
  }
}

export type DrilldownBarLayout =
  | 'simple'
  | 'grouped-vertical'
  | 'stacked-vertical'
  | 'stacked-horizontal'
  | 'grouped-horizontal'

/** Bar width tokens — layout drives vertical column vs grouped-vertical vs horizontal. */
export function drilldownBarDatasetDefaults(
  orientation: 'horizontal' | 'vertical',
  layout: DrilldownBarLayout = 'simple',
):
  | { barPercentage: number; categoryPercentage: number; maxBarThickness: number }
  | { barThickness: number; categoryPercentage: number }
  | {
      barThickness: number
      maxBarThickness: number
      barPercentage: number
      categoryPercentage: number
    } {
  if (orientation === 'vertical') {
    if (layout === 'grouped-vertical') {
      return drilldownGroupedVerticalBarDatasetDefaults()
    }
    return drilldownVerticalColumnBarDatasetDefaults()
  }
  if (layout === 'stacked-horizontal') {
    return drilldownStackedHorizontalBarDatasetDefaults()
  }
  if (layout === 'grouped-horizontal') {
    return drilldownGroupedHorizontalBarDatasetDefaults()
  }
  return drilldownHorizontalBarDatasetDefaults()
}
/** Figma 5897:14811 — inner hole ~50% of diameter (thick ring). */
export const DRILLDOWN_DOUGHNUT_CUTOUT = '50%'

/**
 * Figma 5897:14840–14844 — max-value preview card in the donut hole
 * (title + swatch + "All" + count). Not canvas fillText.
 */
export const DONUT_MAX_PREVIEW_CARD = {
  borderColor: '#F47937',
  borderRadius: '5px',
  bg: '#FFFFFF',
  titleFontSize: '8px',
  subtitleFontSize: '7px',
  countFontSize: '8px',
  lineHeight: '12px',
  fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
  swatchPx: 7,
  subtitle: 'All',
} as const

/** Figma 5917:1468 — multi-segment donut tooltip border. */
export const DONUT_TOOLTIP_BORDER_MULTI = '#F47937'
/** Figma 5917:1469 — binary Yes/No donut tooltip border. */
export const DONUT_TOOLTIP_BORDER_BINARY = '#00ADEE'

export function donutTooltipBorderColor(_segmentCount?: number): string {
  return CHART_PRIMARY
}

/** Draws the dominant segment label in the donut hole (Figma 5917:1468). */
export function createDoughnutCenterLabelPlugin(centerText: string): Plugin<'doughnut'> {
  return {
    id: 'drilldownDoughnutCenterLabel',
    beforeDraw(chart) {
      if (!centerText) return
      const { ctx, chartArea } = chart
      if (!chartArea) return
      const centerX = (chartArea.left + chartArea.right) / 2
      const centerY = (chartArea.top + chartArea.bottom) / 2
      ctx.save()
      ctx.font = 'bold 12px Inter, sans-serif'
      ctx.fillStyle = '#23262F'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(centerText, centerX, centerY)
      ctx.restore()
    },
  }
}
